'use client';

import { useState } from 'react';
import type { ScanEffortLevel } from '@terra/shared-contracts-applications';
import { PlayIcon } from '@terra/ui-icons';
import { HStack, token } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Badge } from '../../../../components/badge';
import { Spinner } from '../../../../components/spinner';
import { ConfirmationDialog } from '../../../confirmation-dialog/ConfirmationDialog';
import { SettingsCard } from '../../SettingsCard';
import { SettingsRow } from '../../SettingsRow';

export type FullRescanControlCardProps = {
  effortLevel: ScanEffortLevel;
  isActive: boolean;
  isStarting: boolean;
  canRescan: boolean;
  onStart: () => Promise<boolean>;
};

export const FullRescanControlCard = ({
  effortLevel,
  isActive,
  isStarting,
  canRescan,
  onStart,
}: FullRescanControlCardProps) => {
  const t = useTranslations('designSystem.scanEffort.fullRescan');
  const tEffort = useTranslations('designSystem.scanEffort');
  const tCommon = useTranslations('designSystem.common');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleConfirm = async () => {
    if (await onStart()) {
      setIsConfirmOpen(false);
    }
  };

  return (
    <>
      <SettingsCard>
        <SettingsRow
          label={t('label')}
          description={canRescan ? t('description') : t('requiresPermission')}
          control={
            isActive ? (
              <Badge
                size="sm"
                bg={token('colors.bg.accent.light')}
                variant="fill"
                color={token('colors.fg.accent.muted')}
              >
                <HStack gap={1} alignItems="center">
                  <Spinner size="sm" borderColor="bg.accent.default" />
                  {t('running')}
                </HStack>
              </Badge>
            ) : undefined
          }
          action={
            isActive
              ? undefined
              : {
                  label: t('action'),
                  icon: <PlayIcon size={16} />,
                  onClick: () => setIsConfirmOpen(true),
                  disabled: isStarting,
                }
          }
          isReadOnly={!canRescan}
        />
      </SettingsCard>
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onOpenChange={({ open }) => setIsConfirmOpen(open)}
        variant="warning"
        title={t('confirmTitle')}
        description={t('confirmDescription', {
          level: tEffort(`levels.${effortLevel}`),
        })}
        confirmText={t('confirm')}
        cancelText={tCommon('cancel')}
        onConfirm={handleConfirm}
        loading={isStarting}
      />
    </>
  );
};
